import React from 'react';
import Icon from '../../../components/AppIcon';

const DailyNutritionSummary = ({ 
  totalCalories, 
  targetCalories, 
  macros,
  hydration 
}) => {
  const calorieProgress = (totalCalories / targetCalories) * 100;
  const waterProgress = (hydration?.consumed / hydration?.target) * 100;

  const macroData = [
    { 
      name: 'Protein', 
      value: macros?.protein?.consumed, 
      target: macros?.protein?.target,
      unit: 'g',
      color: 'bg-accent',
      icon: 'Beef'
    },
    { 
      name: 'Carbs', 
      value: macros?.carbs?.consumed, 
      target: macros?.carbs?.target,
      unit: 'g',
      color: 'bg-warning',
      icon: 'Wheat'
    },
    { 
      name: 'Fats', 
      value: macros?.fats?.consumed, 
      target: macros?.fats?.target,
      unit: 'g',
      color: 'bg-success',
      icon: 'Droplet'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-md p-4 md:p-5 lg:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Daily Summary
        </h2>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
          <Icon name="Calendar" size={16} color="var(--color-primary)" />
          <span className="text-sm font-medium caption text-primary">
            {new Date()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>
      </div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm caption text-muted-foreground">Total Calories</p>
            <p className="text-2xl md:text-3xl font-heading font-bold data-text text-foreground">
              {totalCalories}
              <span className="text-base md:text-lg text-muted-foreground ml-1">/ {targetCalories}</span>
            </p>
          </div>
          <div className="relative w-20 h-20 md:w-24 md:h-24">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="50%"
                cy="50%"
                r="36"
                stroke="var(--color-muted)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="50%"
                cy="50%"
                r="36"
                stroke={calorieProgress > 100 ? 'var(--color-error)' : 'var(--color-primary)'}
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 36}`}
                strokeDashoffset={`${2 * Math.PI * 36 * (1 - Math.min(calorieProgress, 100) / 100)}`}
                strokeLinecap="round"
                className="transition-smooth"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg md:text-xl font-bold data-text text-foreground">
                {Math.round(calorieProgress)}%
              </span>
            </div>
          </div>
        </div>
        <div className="relative h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className={`absolute top-0 left-0 h-full rounded-full transition-smooth ${
              calorieProgress > 100 ? 'bg-error' : 'bg-primary'
            }`}
            style={{ width: `${Math.min(calorieProgress, 100)}%` }}
          />
        </div>
      </div>
      <div className="space-y-4 mb-6">
        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground">
          Macronutrients
        </h3>
        {macroData?.map((macro) => {
          const progress = (macro?.value / macro?.target) * 100;
          return (
            <div key={macro?.name}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`flex items-center justify-center h-8 w-8 rounded-lg ${macro?.color}/10`}>
                    <Icon 
                      name={macro?.icon} 
                      size={16} 
                      className={macro?.color?.replace('bg-', 'text-')}
                    />
                  </div>
                  <span className="text-sm md:text-base font-medium text-foreground">
                    {macro?.name}
                  </span>
                </div>
                <span className="text-sm md:text-base font-semibold data-text text-foreground">
                  {macro?.value}{macro?.unit} / {macro?.target}{macro?.unit}
                </span>
              </div>
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`absolute top-0 left-0 h-full ${macro?.color} rounded-full transition-smooth`}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Droplets" size={20} color="var(--color-primary)" />
            <span className="text-sm md:text-base font-medium text-foreground">
              Hydration
            </span>
          </div>
          <span className="text-sm md:text-base font-semibold data-text text-foreground">
            {hydration?.consumed}L / {hydration?.target}L
          </span>
        </div>
        <div className="relative h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className="absolute top-0 left-0 h-full bg-primary rounded-full transition-smooth"
            style={{ width: `${Math.min(waterProgress, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default DailyNutritionSummary;